import { useEffect, useState } from 'react';
import { getQueries, updateQueryStatus } from '../../../api/adminApi';
import Toast from '../../../components/admin/Toast';

const STATUS_COLORS = {
  pending:  { bg: '#FFF8E7', color: '#B8860B' },
  reviewed: { bg: '#E8F0FF', color: '#3B5BDB' },
  accepted: { bg: '#E8F5E9', color: '#1A7048' },
  rejected: { bg: '#FDE8E8', color: '#C74D3A' },
};

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

export default function ApplicationList() {
  const [apps,       setApps]       = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [filter,     setFilter]     = useState('');       // '' = all
  const [search,     setSearch]     = useState('');
  const [selected,   setSelected]   = useState(null);    // full application shown in drawer
  const [statusMenu, setStatusMenu] = useState(null);    // app id whose dropdown is open
  const [toast,      setToast]      = useState(null);

  const load = (s) =>
    getQueries(s || undefined)
      .then(d => setApps(d.queries))
      .catch(console.error)
      .finally(() => setLoading(false));

  useEffect(() => { load(filter); }, [filter]);

  useEffect(() => {
    if (!statusMenu) return;
    const close = () => setStatusMenu(null);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [statusMenu]);

  async function handleStatus(e, app, newStatus) {
    e.stopPropagation();
    setStatusMenu(null);
    if (app.status === newStatus) return;
    try {
      const res = await updateQueryStatus(app.id, newStatus);
      setApps(prev => prev.map(a => a.id === app.id ? res.query : a));
      if (selected?.id === app.id) setSelected(res.query);
      setToast({ msg: `Status updated to ${newStatus}`, type: 'success' });
    } catch (err) {
      setToast({ msg: err.message, type: 'error' });
    }
  }

  const q = search.toLowerCase();
  const filtered = apps.filter(a =>
    a.full_name?.toLowerCase().includes(q) ||
    a.email?.toLowerCase().includes(q) ||
    a.current_role?.toLowerCase().includes(q) ||
    a.preferred_cohort?.toLowerCase().includes(q)
  );

  return (
    <>
      {/* ── Header ── */}
      <div className="ph">
        <div>
          <div className="bc">Admin / Queries</div>
          <div className="pt">Queries</div>
          <div className="ps">{apps.length} application{apps.length !== 1 ? 's' : ''} received</div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <div className="search-box">
            <input
              placeholder="Search name, email, role…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ width: 220 }}
            />
          </div>
          {/* Status filter tabs */}
          <div style={{ display: 'flex', gap: 4 }}>
            {['', 'pending', 'reviewed', 'accepted', 'rejected'].map(s => (
              <button
                key={s}
                onClick={() => setFilter(s)}
                className={`btn btn-sm ${filter === s ? 'btn-dark' : 'btn-o'}`}
                style={{ textTransform: 'capitalize' }}
              >
                {s || 'All'}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── Table ── */}
      <div className="body">
        <div className="card" style={{ padding: 0 }}>
          <div className="ct" style={{ padding: '16px 20px' }}>
            {search ? `${filtered.length} of ${apps.length} applications` : `${filter ? filter.charAt(0).toUpperCase() + filter.slice(1) : 'All'} Queries`}
          </div>

          {loading ? (
            <div className="empty"><div style={{ color: 'var(--muted)' }}>Loading…</div></div>
          ) : filtered.length === 0 ? (
            <div className="empty">
              <div className="empty-icon">📋</div>
              <div>No applications yet</div>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table>
                <thead>
                  <tr>
                    <th>Applicant</th>
                    <th>Role</th>
                    <th>Experience</th>
                    <th>Cohort</th>
                    <th>Format</th>
                    <th>Submitted</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(a => {
                    const sc = STATUS_COLORS[a.status] || STATUS_COLORS.pending;
                    return (
                      <tr
                        key={a.id}
                        style={{ cursor: 'pointer' }}
                        onClick={() => setSelected(a)}
                      >
                        <td>
                          <div style={{ fontWeight: 500 }}>{a.full_name}</div>
                          <div style={{ fontSize: 11, color: 'var(--muted)' }}>{a.email}</div>
                          <div style={{ fontSize: 11, color: 'var(--muted)' }}>{a.phone}</div>
                        </td>
                        <td style={{ fontSize: 13 }}>{a.current_role}</td>
                        <td style={{ fontSize: 13 }}>{a.experience}</td>
                        <td style={{ fontSize: 12, maxWidth: 180, whiteSpace: 'normal' }}>{a.preferred_cohort}</td>
                        <td style={{ fontSize: 13 }}>{a.kickoff_format}</td>
                        <td style={{ fontSize: 12, color: 'var(--muted)', whiteSpace: 'nowrap' }}>
                          {fmtDate(a.created_at)}
                        </td>
                        <td onClick={e => e.stopPropagation()}>
                          <div style={{ position: 'relative', display: 'inline-block' }}>
                            <span
                              style={{
                                padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                                cursor: 'pointer', userSelect: 'none',
                                background: sc.bg, color: sc.color,
                                textTransform: 'capitalize', display: 'inline-flex', alignItems: 'center', gap: 4,
                              }}
                              onClick={e => { e.stopPropagation(); setStatusMenu(statusMenu === a.id ? null : a.id); }}
                            >
                              {a.status} <span style={{ fontSize: 9, opacity: 0.7 }}>▾</span>
                            </span>
                            {statusMenu === a.id && (
                              <div style={{
                                position: 'absolute', top: '110%', left: 0, zIndex: 100,
                                background: 'var(--card)', border: '1px solid var(--border)',
                                borderRadius: 8, boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                                minWidth: 130, overflow: 'hidden',
                              }}>
                                {['pending', 'reviewed', 'accepted', 'rejected'].map(opt => {
                                  const oc = STATUS_COLORS[opt];
                                  return (
                                    <div
                                      key={opt}
                                      onClick={e => handleStatus(e, a, opt)}
                                      style={{
                                        padding: '9px 14px', fontSize: 13, cursor: 'pointer',
                                        display: 'flex', alignItems: 'center', gap: 8,
                                        background: a.status === opt ? 'var(--surface)' : 'transparent',
                                        fontWeight: a.status === opt ? 600 : 400,
                                        textTransform: 'capitalize',
                                      }}
                                      onMouseEnter={e => e.currentTarget.style.background = 'var(--surface)'}
                                      onMouseLeave={e => e.currentTarget.style.background = a.status === opt ? 'var(--surface)' : 'transparent'}
                                    >
                                      <span style={{ width: 8, height: 8, borderRadius: '50%', background: oc.color, flexShrink: 0 }} />
                                      {opt}
                                      {a.status === opt && <span style={{ marginLeft: 'auto', fontSize: 11 }}>✓</span>}
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* ── Detail Drawer ── */}
      {selected && (
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 200,
            background: 'rgba(0,0,0,0.35)',
            display: 'flex', justifyContent: 'flex-end',
          }}
          onClick={() => setSelected(null)}
        >
          <div
            style={{
              width: '100%', maxWidth: 520, background: 'var(--card)',
              height: '100%', overflowY: 'auto', padding: '28px 28px',
              boxShadow: '-4px 0 24px rgba(0,0,0,0.12)',
            }}
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: 18 }}>{selected.full_name}</div>
                <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: 2 }}>{selected.email}</div>
              </div>
              <button className="btn btn-o btn-sm" onClick={() => setSelected(null)}>✕</button>
            </div>

            {/* Status badge */}
            {(() => {
              const sc = STATUS_COLORS[selected.status] || STATUS_COLORS.pending;
              return (
                <div style={{ marginBottom: 20 }}>
                  <span style={{
                    padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                    background: sc.bg, color: sc.color, textTransform: 'capitalize',
                  }}>
                    {selected.status}
                  </span>
                </div>
              );
            })()}

            {/* Fields */}
            {[
              ['Phone',        selected.phone],
              ['Location',     selected.country_city],
              ['Company',      selected.company || '—'],
              ['Role',         selected.current_role],
              ['Experience',   selected.experience],
              ['Cohort',       selected.preferred_cohort],
              ['Format',       selected.kickoff_format],
              ['How heard',    selected.how_heard || '—'],
              ['Submitted',    fmtDate(selected.created_at)],
              ['Subscribed',   selected.subscribe_updates ? 'Yes' : 'No'],
            ].map(([label, value]) => (
              <div key={label} style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 11, color: 'var(--muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 3 }}>{label}</div>
                <div style={{ fontSize: 13.5 }}>{value}</div>
              </div>
            ))}

            {/* Why AI Foundry */}
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 11, color: 'var(--muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>Why AI Foundry?</div>
              <div style={{ fontSize: 13.5, lineHeight: 1.65, background: 'var(--surface)', padding: '12px 14px', borderRadius: 8, border: '1px solid var(--border)' }}>
                {selected.why_aifoundry}
              </div>
            </div>

            {/* Change status buttons */}
            <div style={{ marginTop: 24 }}>
              <div style={{ fontSize: 11, color: 'var(--muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10 }}>Update Status</div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {['pending', 'reviewed', 'accepted', 'rejected'].map(s => {
                  const sc = STATUS_COLORS[s];
                  return (
                    <button
                      key={s}
                      disabled={selected.status === s}
                      onClick={async (e) => {
                        try {
                          const res = await updateQueryStatus(selected.id, s);
                          setApps(prev => prev.map(a => a.id === selected.id ? res.query : a));
                          setSelected(res.query);
                          setToast({ msg: `Status updated to ${s}`, type: 'success' });
                        } catch (err) {
                          setToast({ msg: err.message, type: 'error' });
                        }
                      }}
                      style={{
                        padding: '6px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                        cursor: selected.status === s ? 'default' : 'pointer',
                        border: `1.5px solid ${sc.color}`,
                        background: selected.status === s ? sc.bg : 'transparent',
                        color: sc.color,
                        textTransform: 'capitalize',
                        opacity: selected.status === s ? 1 : 0.75,
                        transition: 'opacity 0.15s',
                      }}
                      onMouseEnter={e => { if (selected.status !== s) e.currentTarget.style.opacity = '1'; }}
                      onMouseLeave={e => { if (selected.status !== s) e.currentTarget.style.opacity = '0.75'; }}
                    >
                      {selected.status === s ? `✓ ${s}` : s}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <Toast
          message={typeof toast === 'string' ? toast : toast.msg}
          type={typeof toast === 'string' ? 'success' : toast.type}
          onDone={() => setToast(null)}
        />
      )}
    </>
  );
}
